// ============================================================
// KOBFLO ML ENGINE — learns from historical data
// All predictions use your own Supabase data, no external AI
// ============================================================
import { supabase } from './supabase'

// ─── HELPER: simple linear regression ────────────────────────
function linearRegression(points) {
  // points = [{x, y}]
  const n = points.length
  if (n < 2) return { slope: 0, intercept: points[0]?.y || 0, r2: 0 }
  const sumX = points.reduce((s, p) => s + p.x, 0)
  const sumY = points.reduce((s, p) => s + p.y, 0)
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0)
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0)
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n
  // R² — how well the line fits
  const yMean = sumY / n
  const ssTot = points.reduce((s, p) => s + Math.pow(p.y - yMean, 2), 0)
  const ssRes = points.reduce((s, p) => s + Math.pow(p.y - (slope * p.x + intercept), 2), 0)
  const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot
  return { slope, intercept, r2 }
}

function average(arr) {
  if (!arr.length) return 0
  return arr.reduce((s, v) => s + v, 0) / arr.length
}

function stdDev(arr) {
  const avg = average(arr)
  return Math.sqrt(average(arr.map(v => Math.pow(v - avg, 2))))
}

// ─── 1. ACCRUAL EXPENSE PREDICTOR ────────────────────────────
// Looks at past reconciled provisions for each category
// and predicts this month's amount with confidence level
export async function predictAccrualExpenses() {
  const { data } = await supabase
    .from('accrual_provisions')
    .select('category, actual_amount, estimated_amount, period_month, is_reconciled')
    .eq('is_reconciled', true)
    .order('period_month', { ascending: true })

  if (!data || data.length < 2) return []

  // Group by category
  const byCategory = {}
  data.forEach(p => {
    if (!byCategory[p.category]) byCategory[p.category] = []
    byCategory[p.category].push(parseFloat(p.actual_amount || p.estimated_amount))
  })

  return Object.entries(byCategory).map(([category, amounts]) => {
    const avg = average(amounts)
    const std = stdDev(amounts)
    const last3 = amounts.slice(-3)
    const trend = amounts.length >= 3
      ? linearRegression(amounts.map((y, x) => ({ x, y })))
      : null

    // Predicted = weighted average (recent months count more)
    const weights = last3.map((_, i) => i + 1)
    const weightSum = weights.reduce((s, w) => s + w, 0)
    const predicted = last3.reduce((s, v, i) => s + v * weights[i], 0) / weightSum

    // Confidence based on how consistent past values are
    const cv = avg > 0 ? std / avg : 1 // coefficient of variation
    const confidence = cv < 0.1 ? 'High' : cv < 0.25 ? 'Medium' : 'Low'
    const confidenceColor = { High: 'var(--green-mid)', Medium: 'var(--amber)', Low: 'var(--red)' }[confidence]

    const trendDir = trend && Math.abs(trend.slope) > avg * 0.05
      ? trend.slope > 0 ? '↑ trending up' : '↓ trending down'
      : '→ stable'

    return {
      category,
      predicted: Math.round(predicted),
      average: Math.round(avg),
      min: Math.round(Math.min(...amounts)),
      max: Math.round(Math.max(...amounts)),
      dataPoints: amounts.length,
      confidence,
      confidenceColor,
      trend: trendDir,
      history: amounts,
    }
  })
}

// ─── 2. WEEKLY INCOME FORECAST ───────────────────────────────
// Learns from past weekly P&L to predict next week
export async function forecastWeeklyIncome() {
  const { data } = await supabase
    .from('weekly_pl')
    .select('week_ending, total_income, total_expenses, net')
    .order('week_ending', { ascending: true })
    .limit(24) // last 6 months of weeks

  if (!data || data.length < 3) return null

  const incomes = data.map(r => parseFloat(r.total_income || 0))
  const expenses = data.map(r => parseFloat(r.total_expenses || 0))
  const nets = data.map(r => parseFloat(r.net || 0))

  const incomeRegression = linearRegression(incomes.map((y, x) => ({ x, y })))
  const expenseRegression = linearRegression(expenses.map((y, x) => ({ x, y })))

  const nextX = incomes.length
  const predictedIncome = Math.max(0, Math.round(incomeRegression.slope * nextX + incomeRegression.intercept))
  const predictedExpenses = Math.max(0, Math.round(expenseRegression.slope * nextX + expenseRegression.intercept))

  // Seasonal pattern — same week last month
  const last4Incomes = incomes.slice(-4)
  const seasonalIncome = Math.round(average(last4Incomes))

  // Blend regression + seasonal
  const blendedIncome = Math.round((predictedIncome * 0.4) + (seasonalIncome * 0.6))

  const bestWeek = Math.max(...incomes)
  const worstWeek = Math.min(...incomes)
  const avgNet = Math.round(average(nets))

  return {
    predictedIncome: blendedIncome,
    predictedExpenses,
    predictedNet: blendedIncome - predictedExpenses,
    bestWeek: Math.round(bestWeek),
    worstWeek: Math.round(worstWeek),
    avgNet,
    trend: incomeRegression.slope > 1000 ? '↑ Growing' : incomeRegression.slope < -1000 ? '↓ Declining' : '→ Stable',
    trendColor: incomeRegression.slope > 1000 ? 'var(--green-mid)' : incomeRegression.slope < -1000 ? 'var(--red)' : 'var(--amber)',
    weeksOfData: data.length,
    r2: Math.round(incomeRegression.r2 * 100),
  }
}

// ─── 3. HARVEST YIELD PREDICTOR ──────────────────────────────
// Uses farm log readings (pH, EC) + past harvests to predict yield
export async function predictHarvestYield() {
  const { data: harvests } = await supabase
    .from('harvest_logs')
    .select('harvest_date, crop_id, total_kg, grade_a_kg, grade_b_kg, crops(name)')
    .order('harvest_date', { ascending: true })
    .limit(60)

  if (!harvests || harvests.length < 4) return []

  // Group by crop
  const byCrop = {}
  harvests.forEach(h => {
    const name = h.crops?.name || 'Unknown'
    if (!byCrop[name]) byCrop[name] = []
    byCrop[name].push({
      date: new Date(h.harvest_date),
      total: parseFloat(h.total_kg || 0),
      gradeA: parseFloat(h.grade_a_kg || 0),
      gradeB: parseFloat(h.grade_b_kg || 0),
    })
  })

  return Object.entries(byCrop).map(([cropName, records]) => {
    const totals = records.map(r => r.total)
    const gradeARatios = records.map(r => r.total > 0 ? r.gradeA / r.total : 0)

    const avgYield = average(totals)
    const avgGradeA = Math.round(average(gradeARatios) * 100)
    const regression = linearRegression(totals.map((y, x) => ({ x, y })))

    const nextPredicted = Math.max(0, Math.round(
      regression.slope * totals.length + regression.intercept
    ))

    // Days between harvests
    const gaps = records.slice(1).map((r, i) =>
      (r.date - records[i].date) / (1000 * 60 * 60 * 24)
    )
    const avgGap = gaps.length ? Math.round(average(gaps)) : null

    const trend = regression.slope > 0.5 ? '↑ improving' : regression.slope < -0.5 ? '↓ declining' : '→ consistent'

    return {
      cropName,
      predictedYield: nextPredicted,
      averageYield: Math.round(avgYield),
      bestHarvest: Math.round(Math.max(...totals)),
      gradeAPercent: avgGradeA,
      trend,
      avgDaysBetweenHarvests: avgGap,
      dataPoints: records.length,
      lastHarvest: records[records.length - 1]?.date,
    }
  })
}

// ─── 4. CUSTOMER PAYMENT RISK SCORER ─────────────────────────
// Scores each customer's payment reliability 0-100
export async function scoreCustomerPaymentRisk() {
  const { data: invoices } = await supabase
    .from('invoices')
    .select('customer_name, customer_id, status, payment_terms, invoice_date, due_date, amount, balance_due, invoice_payments(*)')
    .order('invoice_date', { ascending: true })

  if (!invoices || !invoices.length) return []

  const byCustomer = {}
  invoices.forEach(inv => {
    const key = inv.customer_name
    if (!byCustomer[key]) byCustomer[key] = { name: key, invoices: [] }
    byCustomer[key].invoices.push(inv)
  })

  return Object.values(byCustomer).map(({ name, invoices: invs }) => {
    const total = invs.length
    const paid = invs.filter(i => i.status === 'Paid').length
    const overdue = invs.filter(i => i.status === 'Overdue').length
    const disputed = invs.filter(i => i.status === 'Disputed').length

    // Average days to pay (for paid invoices)
    const daysToPayArr = invs
      .filter(i => i.status === 'Paid' && i.invoice_payments?.length)
      .map(i => {
        const lastPayment = i.invoice_payments[i.invoice_payments.length - 1]
        return (new Date(lastPayment.payment_date) - new Date(i.invoice_date)) / (1000 * 60 * 60 * 24)
      })
    const avgDaysToPay = daysToPayArr.length ? Math.round(average(daysToPayArr)) : null

    // Risk score (0 = high risk, 100 = very reliable)
    let score = 100
    if (total > 0) {
      score -= (overdue / total) * 40   // overdue hurts a lot
      score -= (disputed / total) * 30  // disputes hurt
      score += (paid / total) * 20      // good payment history helps
      if (avgDaysToPay !== null) {
        const avgTerms = average(invs.map(i => i.payment_terms || 30))
        if (avgDaysToPay > avgTerms * 1.5) score -= 20 // consistently late
        else if (avgDaysToPay < avgTerms) score += 10   // consistently early
      }
    }
    score = Math.max(0, Math.min(100, Math.round(score)))

    const riskLevel = score >= 75 ? 'Low Risk' : score >= 50 ? 'Medium Risk' : 'High Risk'
    const riskColor = score >= 75 ? 'var(--green-mid)' : score >= 50 ? 'var(--amber)' : 'var(--red)'

    return {
      name,
      score,
      riskLevel,
      riskColor,
      totalInvoices: total,
      paidOnTime: paid,
      overdueCount: overdue,
      avgDaysToPay,
      recommendation: score < 50
        ? 'Consider requiring payment on delivery'
        : score < 75
          ? 'Monitor closely — send reminders early'
          : 'Reliable customer — standard terms OK',
    }
  }).sort((a, b) => a.score - b.score) // highest risk first
}

// ─── 5. SMART REORDER QUANTITY ────────────────────────────────
// Analyses actual consumption rate to suggest better reorder quantities
export async function smartReorderQuantities() {
  const { data: movements } = await supabase
    .from('stock_movements')
    .select('stock_item_id, movement_type, quantity, movement_date, stock_items(name, unit, reorder_level, reorder_qty)')
    .eq('movement_type', 'out')
    .order('movement_date', { ascending: true })
    .limit(200)

  if (!movements || !movements.length) return []

  const byItem = {}
  movements.forEach(m => {
    const id = m.stock_item_id
    if (!id || !m.stock_items) return
    if (!byItem[id]) byItem[id] = { item: m.stock_items, usages: [] }
    byItem[id].usages.push({
      date: new Date(m.movement_date),
      qty: parseFloat(m.quantity || 0)
    })
  })

  return Object.values(byItem).map(({ item, usages }) => {
    if (usages.length < 2) return null

    // Weekly consumption rate
    const totalQty = usages.reduce((s, u) => s + u.qty, 0)
    const daySpan = (usages[usages.length - 1].date - usages[0].date) / (1000 * 60 * 60 * 24)
    const weeklyRate = daySpan > 0 ? (totalQty / daySpan) * 7 : 0

    // Suggested reorder = 3 weeks supply (with 1 week buffer)
    const suggestedQty = Math.ceil(weeklyRate * 3)
    const currentQty = parseFloat(item.reorder_qty || 0)
    const diff = suggestedQty - currentQty
    const changeNeeded = Math.abs(diff) > currentQty * 0.2 // >20% difference

    return {
      name: item.name,
      unit: item.unit,
      weeklyUsageRate: Math.round(weeklyRate * 10) / 10,
      currentReorderQty: currentQty,
      suggestedReorderQty: suggestedQty,
      changeNeeded,
      suggestion: diff > 0
        ? `↑ Increase to ${suggestedQty} ${item.unit} — running out faster than expected`
        : diff < 0
          ? `↓ Reduce to ${suggestedQty} ${item.unit} — overstocking`
          : `✓ Current quantity is optimal`,
      dataPoints: usages.length,
    }
  }).filter(Boolean).filter(r => r.changeNeeded)
}

// ─── 6. CROP HEALTH TREND ALERT ──────────────────────────────
// Spots deteriorating pH/EC trends before they become problems
export async function cropHealthTrends() {
  // Reads from farm_logs linked to crop_cycles
  const { data } = await supabase
    .from('farm_logs')
    .select('ph, ec, water_temp, plant_health, log_date, created_at, crop_cycles(cycle_name, crop)')
    .order('log_date', { ascending: true })
    .limit(100)

  if (!data || !data.length) return []

  const byCrop = {}
  data.forEach(e => {
    const name = e.crop_cycles?.cycle_name || e.crop_cycles?.crop || 'Unknown'
    if (!byCrop[name]) byCrop[name] = { crop: { name, ph_min: 5.5, ph_max: 6.5, ec_min: 1.5, ec_max: 2.5 }, readings: [] }
    byCrop[name].readings.push({
      date: new Date(e.log_date || e.created_at),
      ph: parseFloat(e.ph) || null,
      ec: parseFloat(e.ec) || null,
      temp: parseFloat(e.water_temp) || null,
      health: e.plant_health,
    })
  })

  return Object.entries(byCrop).map(([cropName, { crop, readings }]) => {
    const last7 = readings.slice(-7)
    const alerts = []

    // pH trend
    const phReadings = last7.filter(r => r.ph).map((r, i) => ({ x: i, y: r.ph }))
    if (phReadings.length >= 3) {
      const phReg = linearRegression(phReadings)
      const currentPh = phReadings[phReadings.length - 1]?.y
      if (Math.abs(phReg.slope) > 0.05) {
        alerts.push({
          type: 'pH',
          message: `pH ${phReg.slope > 0 ? 'rising' : 'falling'} (${phReg.slope > 0 ? '+' : ''}${phReg.slope.toFixed(2)}/day) — currently ${currentPh?.toFixed(1)}. Target: ${crop.ph_min}–${crop.ph_max}`,
          severity: Math.abs(phReg.slope) > 0.1 ? 'High' : 'Medium',
        })
      }
    }

    // EC trend
    const ecReadings = last7.filter(r => r.ec).map((r, i) => ({ x: i, y: r.ec }))
    if (ecReadings.length >= 3) {
      const ecReg = linearRegression(ecReadings)
      const currentEc = ecReadings[ecReadings.length - 1]?.y
      if (Math.abs(ecReg.slope) > 0.05) {
        alerts.push({
          type: 'EC',
          message: `EC ${ecReg.slope > 0 ? 'rising' : 'falling'} (${ecReg.slope > 0 ? '+' : ''}${ecReg.slope.toFixed(2)}/day) — currently ${currentEc?.toFixed(2)}. Target: ${crop.ec_min}–${crop.ec_max}`,
          severity: Math.abs(ecReg.slope) > 0.1 ? 'High' : 'Medium',
        })
      }
    }

    // Health deterioration
    const healthMap = { 'Excellent — all looking great': 4, 'Good — minor issues': 3, 'Concerning — action needed': 2, 'Poor — urgent intervention': 1 }
    const healthScores = last7.filter(r => r.health).map((r, i) => ({ x: i, y: healthMap[r.health] || 3 }))
    if (healthScores.length >= 3) {
      const healthReg = linearRegression(healthScores)
      if (healthReg.slope < -0.1) {
        alerts.push({
          type: 'Plant Health',
          message: `Plant health declining over last ${healthScores.length} readings — intervention may be needed`,
          severity: healthReg.slope < -0.2 ? 'High' : 'Medium',
        })
      }
    }

    return { cropName, alerts, readingCount: readings.length }
  }).filter(r => r.alerts.length > 0)
}
